import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { appointmentFlowApi } from '../../../api';
import { useUIStore } from '../../../store';

const emptyForm = {
  branchId: '',
  serviceId: '',
  employeeId: '',
  bookingDate: '',
  bookingTime: '',
  customerName: '',
  location: '',
};

const flowMessageDefaults = {
  title: 'Appointment',
  branchPrompt: 'Select a branch to continue.',
  servicePrompt: 'Select a service.',
  employeePrompt: 'Select an employee.',
  slotPrompt: 'Select a date and time.',
};

const emptyFlowMessages = {
  title: '',
  branchPrompt: '',
  servicePrompt: '',
  employeePrompt: '',
  slotPrompt: '',
};

const AppointmentFlowPage = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [flowMessages, setFlowMessages] = useState(emptyFlowMessages);
  const [flowDefaults, setFlowDefaults] = useState(flowMessageDefaults);
  const [flowLoading, setFlowLoading] = useState(false);
  const [flowSaving, setFlowSaving] = useState(false);
  const [flowError, setFlowError] = useState('');
  const [loading, setLoading] = useState({
    branches: false,
    services: false,
    employees: false,
    slots: false,
    submit: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const showSnackbar = useUIStore((state) => state.showSnackbar);

  const selectedBranch = useMemo(
    () => branches.find((item) => String(item.id) === String(form.branchId)) || null,
    [branches, form.branchId]
  );
  const selectedService = useMemo(
    () => services.find((item) => String(item.id) === String(form.serviceId)) || null,
    [services, form.serviceId]
  );
  const selectedEmployee = useMemo(
    () => employees.find((item) => String(item.id) === String(form.employeeId)) || null,
    [employees, form.employeeId]
  );

  const availableDates = useMemo(() => {
    const seen = new Set();
    const dates = [];
    slots.forEach((slot) => {
      if (!slot?.date || seen.has(slot.date)) return;
      seen.add(slot.date);
      dates.push(slot.date);
    });
    return dates;
  }, [slots]);

  const availableTimes = useMemo(() => {
    if (!form.bookingDate) return [];
    return slots.filter((slot) => slot.date === form.bookingDate);
  }, [slots, form.bookingDate]);

  const fetchBranches = async () => {
    setLoading((prev) => ({ ...prev, branches: true }));
    setError('');
    try {
      const response = await appointmentFlowApi.getBranches({ status: 'active' });
      const data = response?.data?.data || response?.data || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      setBranches([]);
      setError('Failed to load branches.');
    } finally {
      setLoading((prev) => ({ ...prev, branches: false }));
    }
  };

  const fetchServices = async (branchId) => {
    setLoading((prev) => ({ ...prev, services: true }));
    setError('');
    try {
      const response = await appointmentFlowApi.getServices({ branchId, status: 'active' });
      const data = response?.data?.data || response?.data || [];
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      setServices([]);
      setError('Failed to load services.');
    } finally {
      setLoading((prev) => ({ ...prev, services: false }));
    }
  };

  const fetchEmployees = async (branchId, serviceId) => {
    setLoading((prev) => ({ ...prev, employees: true }));
    setError('');
    try {
      const response = await appointmentFlowApi.getEmployees({
        branchId,
        serviceId,
        status: 'active',
      });
      const data = response?.data?.data || response?.data || [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setEmployees([]);
      setError('Failed to load employees.');
    } finally {
      setLoading((prev) => ({ ...prev, employees: false }));
    }
  };

  const fetchSlots = async (payload) => {
    setLoading((prev) => ({ ...prev, slots: true }));
    setError('');
    try {
      const response = await appointmentFlowApi.getAvailableSlots(payload);
      const data = response?.data?.data || response?.data || [];
      setSlots(Array.isArray(data) ? data : []);
    } catch (err) {
      setSlots([]);
      setError('Failed to load available slots.');
    } finally {
      setLoading((prev) => ({ ...prev, slots: false }));
    }
  };

  const fetchFlowMessages = async () => {
    setFlowLoading(true);
    setFlowError('');
    try {
      const response = await appointmentFlowApi.getFlowMessages();
      const data = response?.data?.data || {};
      const values = data.values && typeof data.values === 'object' ? data.values : {};
      const defaults = data.defaults && typeof data.defaults === 'object' ? data.defaults : {};
      setFlowMessages({ ...emptyFlowMessages, ...values });
      setFlowDefaults({ ...flowMessageDefaults, ...defaults });
    } catch (err) {
      setFlowMessages(emptyFlowMessages);
      setFlowDefaults(flowMessageDefaults);
      setFlowError('Failed to load appointment flow messages.');
    } finally {
      setFlowLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchFlowMessages();
  }, []);

  useEffect(() => {
    if (!success) return;
    showSnackbar({ message: success, severity: 'success' });
    setSuccess('');
  }, [showSnackbar, success]);

  const handleBranchChange = (event) => {
    const branchId = event.target.value;
    setForm((prev) => ({
      ...prev,
      branchId,
      serviceId: '',
      employeeId: '',
      bookingDate: '',
      bookingTime: '',
    }));
    setServices([]);
    setEmployees([]);
    setSlots([]);
    if (branchId) {
      fetchServices(branchId);
    }
  };

  const handleServiceChange = (event) => {
    const serviceId = event.target.value;
    setForm((prev) => ({
      ...prev,
      serviceId,
      employeeId: '',
      bookingDate: '',
      bookingTime: '',
    }));
    setEmployees([]);
    setSlots([]);
    if (form.branchId && serviceId) {
      fetchEmployees(form.branchId, serviceId);
    }
  };

  const handleEmployeeChange = (event) => {
    const employeeId = event.target.value;
    setForm((prev) => ({
      ...prev,
      employeeId,
      bookingDate: '',
      bookingTime: '',
    }));
    setSlots([]);
    if (form.branchId && form.serviceId && employeeId) {
      fetchSlots({
        branchId: form.branchId,
        serviceId: form.serviceId,
        employeeId,
      });
    }
  };

  const handleFlowMessageChange = (field) => (event) => {
    setFlowMessages((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveFlowMessages = async () => {
    setFlowError('');
    setFlowSaving(true);
    try {
      const response = await appointmentFlowApi.updateFlowMessages(flowMessages);
      const data = response?.data?.data || {};
      const values = data.values && typeof data.values === 'object' ? data.values : {};
      const defaults = data.defaults && typeof data.defaults === 'object' ? data.defaults : {};
      setFlowMessages({ ...emptyFlowMessages, ...values });
      setFlowDefaults({ ...flowMessageDefaults, ...defaults });
      showSnackbar({ message: 'Appointment flow messages saved.', severity: 'success' });
    } catch (err) {
      setFlowError(err?.response?.data?.message || 'Failed to update appointment flow messages.');
    } finally {
      setFlowSaving(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (
      !form.branchId ||
      !form.serviceId ||
      !form.employeeId ||
      !form.bookingDate ||
      !form.bookingTime ||
      !form.customerName.trim()
    ) {
      setError('Please complete all required fields before submitting.');
      return;
    }

    setLoading((prev) => ({ ...prev, submit: true }));
    try {
      await appointmentFlowApi.createAppointment({
        branchId: form.branchId,
        serviceId: form.serviceId,
        employeeId: form.employeeId,
        bookingDate: form.bookingDate,
        bookingTime: form.bookingTime,
        customerName: form.customerName,
        location: form.location,
      });
      setSuccess('Appointment booked successfully.');
      setForm((prev) => ({
        ...prev,
        bookingDate: '',
        bookingTime: '',
        customerName: '',
        location: '',
      }));
      setSlots([]);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to submit appointment.');
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Button
          variant="text"
          startIcon={<ArrowLeft />}
          onClick={() => navigate(-1)}
          sx={{ minWidth: 0, px: 0 }}
        >
          Back
        </Button>
        <Typography variant="h5">Appointment Flow</Typography>
      </Stack>

      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1">Appointment Bot Messages</Typography>
              <Typography variant="body2" color="text.secondary">
                Leave a field blank to use the default WhatsApp prompt.
              </Typography>

              {flowError && <Alert severity="error">{flowError}</Alert>}

              {flowLoading && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading message settings...</Typography>
                </Stack>
              )}

              <TextField
                label="Header title"
                size="small"
                fullWidth
                value={flowMessages.title}
                onChange={handleFlowMessageChange('title')}
                placeholder={flowDefaults.title}
                helperText={`Default: ${flowDefaults.title}`}
                disabled={flowLoading || flowSaving}
              />

              <TextField
                label="Branch prompt"
                size="small"
                fullWidth
                value={flowMessages.branchPrompt}
                onChange={handleFlowMessageChange('branchPrompt')}
                placeholder={flowDefaults.branchPrompt}
                helperText={`Default: ${flowDefaults.branchPrompt}`}
                disabled={flowLoading || flowSaving}
              />

              <TextField
                label="Service prompt"
                size="small"
                fullWidth
                value={flowMessages.servicePrompt}
                onChange={handleFlowMessageChange('servicePrompt')}
                placeholder={flowDefaults.servicePrompt}
                helperText={`Default: ${flowDefaults.servicePrompt}`}
                disabled={flowLoading || flowSaving}
              />

              <TextField
                label="Employee prompt"
                size="small"
                fullWidth
                value={flowMessages.employeePrompt}
                onChange={handleFlowMessageChange('employeePrompt')}
                placeholder={flowDefaults.employeePrompt}
                helperText={`Default: ${flowDefaults.employeePrompt}`}
                disabled={flowLoading || flowSaving}
              />

              <TextField
                label="Slot prompt"
                size="small"
                fullWidth
                value={flowMessages.slotPrompt}
                onChange={handleFlowMessageChange('slotPrompt')}
                placeholder={flowDefaults.slotPrompt}
                helperText={`Default: ${flowDefaults.slotPrompt}`}
                disabled={flowLoading || flowSaving}
              />

              <Button
                variant="contained"
                onClick={handleSaveFlowMessages}
                disabled={flowLoading || flowSaving}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                {flowSaving ? 'Saving...' : 'Save Messages'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}

              <FormControl size="small" fullWidth disabled={loading.branches}>
                <InputLabel>Branch</InputLabel>
                <Select label="Branch" value={form.branchId} onChange={handleBranchChange}>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth disabled={!form.branchId || loading.services}>
                <InputLabel>Service</InputLabel>
                <Select label="Service" value={form.serviceId} onChange={handleServiceChange}>
                  {services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth disabled={!form.serviceId || loading.employees}>
                <InputLabel>Employee</InputLabel>
                <Select label="Employee" value={form.employeeId} onChange={handleEmployeeChange}>
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControl size="small" fullWidth disabled={!slots.length}>
                  <InputLabel>Date</InputLabel>
                  <Select
                    label="Date"
                    value={form.bookingDate}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        bookingDate: event.target.value,
                        bookingTime: '',
                      }))
                    }
                  >
                    {availableDates.map((date) => (
                      <MenuItem key={date} value={date}>
                        {date}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth disabled={!form.bookingDate}>
                  <InputLabel>Time</InputLabel>
                  <Select
                    label="Time"
                    value={form.bookingTime}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, bookingTime: event.target.value }))
                    }
                  >
                    {availableTimes.map((slot) => (
                      <MenuItem key={`${slot.date}-${slot.start}`} value={slot.start}>
                        {slot.start} - {slot.end}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {loading.slots && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading slots...</Typography>
                </Stack>
              )}

              {!loading.slots && form.employeeId && !slots.length && (
                <Alert severity="info">No available slots were found for this employee.</Alert>
              )}

              <TextField
                label="Customer Name"
                size="small"
                fullWidth
                value={form.customerName}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, customerName: event.target.value }))
                }
              />

              <TextField
                label="Location"
                size="small"
                fullWidth
                value={form.location}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, location: event.target.value }))
                }
              />

              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.4)' }}>
                <Typography variant="caption" color="text.secondary">
                  Summary
                </Typography>
                <Typography variant="body2">
                  {selectedBranch?.name || 'Branch'} / {selectedService?.name || 'Service'}
                </Typography>
                <Typography variant="body2">
                  {selectedEmployee?.name || 'Employee'} / {form.bookingDate || 'Date'} /{' '}
                  {form.bookingTime || 'Time'}
                </Typography>
              </Box>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading.submit}
                sx={{ textTransform: 'none' }}
              >
                {loading.submit ? 'Submitting...' : 'Submit Appointment'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default AppointmentFlowPage;
